import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import {
  MagnifyingGlass, ChartBar, Star, ArrowRight, Trash,
  Flame, Eye, X, ImageBroken, Sword, Trophy, Warning, DownloadSimple, Share,
} from '@phosphor-icons/react';
import ShareButtons from '../components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/heatmap`;

const GRADE_COLOR = {
  A: '#10B981',
  B: '#3B82F6',
  C: '#F59E0B',
  D: '#F97316',
  F: '#EF4444',
};

// Hex with alpha helper — keeps tailwind happy by using inline RGBA.
const intensityToColor = (intensity) => {
  // 0 → blue-ish (cold), 0.5 → orange, 1 → red
  if (intensity >= 0.8) return ['rgba(239,68,68,', '0.85)']; // red-500
  if (intensity >= 0.6) return ['rgba(249,115,22,', '0.8)']; // orange-500
  if (intensity >= 0.4) return ['rgba(245,158,11,', '0.75)']; // amber-500
  return ['rgba(59,130,246,', '0.7)']; // blue-500
};


// ─────────────────────── Heatmap canvas overlay ───────────────────────


const HeatmapOverlay = ({ result, onZoneClick, activeZoneIdx, t }) => {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => { setImgError(false); setImgLoaded(false); }, [result?.heatmap_id]);

  if (!result) return null;

  return (
    <div className="relative w-full bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden" data-testid="heatmap-canvas">
      <div className="relative w-full" style={{ aspectRatio: '1200 / 800' }}>
        {imgError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/40 text-sm gap-2">
            <ImageBroken size={32} weight="light" />
            <span data-testid="screenshot-fallback">{t('heatmap.screenshot_failed')}</span>
          </div>
        ) : (
          <>
            {!imgLoaded && (
              <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm">
                {t('heatmap.screenshot_loading')}
              </div>
            )}
            <img
              src={result.screenshot_url}
              alt="Landing screenshot"
              className="absolute inset-0 w-full h-full object-contain"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              referrerPolicy="no-referrer"
              data-testid="heatmap-screenshot"
            />
          </>
        )}

        {/* Hot zones overlay */}
        <div className="absolute inset-0 pointer-events-none">
          {(result.zones || []).map((z, idx) => {
            const [base, end] = intensityToColor(z.intensity);
            const isActive = idx === activeZoneIdx;
            return (
              <button
                key={idx}
                onClick={() => onZoneClick(idx)}
                data-testid={`hot-zone-${idx}`}
                className="absolute rounded-full pointer-events-auto transition-all cursor-pointer group"
                style={{
                  left: `${z.x * 100}%`,
                  top: `${z.y * 100}%`,
                  width: `${z.radius * 200}%`,
                  height: `${z.radius * 200}%`,
                  transform: 'translate(-50%, -50%)',
                  backgroundImage: `radial-gradient(circle, ${base}${z.intensity}) 0%, ${base}0.4) 35%, ${base}0) 75%)`,
                  outline: isActive ? `2px solid ${base}1)` : 'none',
                  zIndex: 10 - idx,
                }}
              >
                <span
                  className={`absolute -top-1 -left-1 text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center text-white shadow-lg transition-opacity ${isActive ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`}
                  style={{ background: `${base}1)` }}
                >
                  {idx + 1}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};


// ─────────────────────── Zone list ───────────────────────


const ZoneList = ({ zones, activeIdx, onPick, t }) => (
  <div className="space-y-2" data-testid="zone-list">
    {zones.map((z, idx) => {
      const [base] = intensityToColor(z.intensity);
      const pct = Math.round(z.intensity * 100);
      const active = idx === activeIdx;
      return (
        <button
          key={idx}
          onClick={() => onPick(idx)}
          data-testid={`zone-item-${idx}`}
          className={`w-full text-left p-3 rounded-xl border transition-all ${active ? 'border-white/30 bg-white/5' : 'border-white/10 bg-[#0A0A0A] hover:border-white/20'}`}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 mt-0.5"
              style={{ background: `${base}1)` }}
            >
              {idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-semibold text-white truncate">{z.label}</span>
                <span className="text-[11px] font-bold shrink-0" style={{ color: `${base}1)` }}>
                  {pct}%
                </span>
              </div>
              {/* Intensity bar */}
              <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full transition-all"
                  style={{ width: `${pct}%`, background: `${base}1)` }}
                />
              </div>
              {z.reasoning && (
                <p className="text-xs text-white/60 leading-relaxed">{z.reasoning}</p>
              )}
            </div>
          </div>
        </button>
      );
    })}
  </div>
);


// ─────────────────────── Stat card ───────────────────────


const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div
    className="p-4 bg-[#0F0F0F] border border-white/10 rounded-xl"
    style={{ borderColor: `${accent}40` }}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon size={12} weight="bold" style={{ color: accent }} />
      <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{label}</span>
    </div>
    <div className="text-xl font-[Outfit] font-bold" style={{ color: accent }}>{value}</div>
  </div>
);


// ─────────────────────── Comparison view ───────────────────────


const SEVERITY_COLOR = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#3B82F6',
  general: '#6B7280',
};


const ComparisonView = ({ data, activeIdx, activeIdxCompetitor, onPickPrimary, onPickCompetitor, t, forwardRef }) => {
  const { primary, competitor, insights } = data;
  const verdictKey = insights?.verdict || 'tie';
  const verdictMap = {
    primary: { label: t('heatmap.verdict_primary'), color: '#10B981', icon: Trophy },
    competitor: { label: t('heatmap.verdict_competitor'), color: '#EF4444', icon: Warning },
    tie: { label: t('heatmap.verdict_tie'), color: '#A855F7', icon: Sword },
  };
  const vd = verdictMap[verdictKey] || verdictMap.tie;
  const VerdictIcon = vd.icon;
  const m = insights?.metrics || {};
  const [downloading, setDownloading] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [mintingShare, setMintingShare] = useState(false);

  const ensureShareLink = async () => {
    if (shareData) return shareData;
    setMintingShare(true);
    try {
      const res = await axios.post(
        `${API}/share-link`,
        { primary_url: primary.url, competitor_url: competitor.url },
        { withCredentials: true },
      );
      setShareData(res.data);
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error_generic'));
      return null;
    } finally {
      setMintingShare(false);
    }
  };

  const downloadShareCard = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/share-card`,
        { primary_url: primary.url, competitor_url: competitor.url },
        { withCredentials: true, responseType: 'blob' },
      );
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'funnelhack-battle.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(t('heatmap.share_card_downloaded'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error_generic'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div ref={forwardRef} data-testid="heatmap-comparison" className="space-y-5">
      {/* Verdict banner */}
      <div
        className="p-6 rounded-2xl border flex items-center gap-5"
        style={{ borderColor: `${vd.color}55`, background: `linear-gradient(135deg, ${vd.color}1a, transparent)` }}
        data-testid="comparison-verdict"
      >
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: `${vd.color}22`, color: vd.color }}
        >
          <VerdictIcon size={26} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-semibold mb-1">
            {t('heatmap.battle_score')}
          </div>
          <div className="text-2xl font-[Outfit] font-bold text-white" data-testid="verdict-label">
            {vd.label}
          </div>
          <div className="text-xs text-white/60 mt-1">
            <span className="text-[#3B82F6] font-bold">{Math.round(insights?.primary_score || 0)}</span>
            <span className="mx-2 text-white/30">vs</span>
            <span className="text-[#EF4444] font-bold">{Math.round(insights?.competitor_score || 0)}</span>
            <span className="ml-2 text-white/40">· Δ {insights?.diff > 0 ? '+' : ''}{insights?.diff}</span>
          </div>
        </div>
        <button
          onClick={downloadShareCard}
          disabled={downloading}
          data-testid="share-card-btn"
          className="shrink-0 px-4 py-2.5 bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white text-xs font-semibold rounded-lg flex items-center gap-2 transition-all border border-white/15"
        >
          {downloading ? (
            <>
              <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              {t('heatmap.generating_share_card')}
            </>
          ) : (
            <>
              <DownloadSimple size={14} weight="bold" />
              {t('heatmap.share_card')}
            </>
          )}
        </button>
      </div>

      {/* Social share row — lazily mints a share-link on demand */}
      <div className="p-4 rounded-2xl bg-[#0A0A0A] border border-white/10 flex items-center justify-between gap-3 flex-wrap" data-testid="comparison-share-row">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#A855F7]/15 flex items-center justify-center text-[#C084FC]">
            <Share size={15} weight="bold" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white">{t('heatmap.share_this_battle')}</div>
            <div className="text-xs text-white/50">{t('heatmap.share_this_battle_sub')}</div>
          </div>
        </div>
        {shareData ? (
          <ShareButtons
            shareUrl={`${window.location.origin}/api/share/${shareData.token}/preview`}
            verdict={shareData.verdict}
            primaryScore={Math.round(shareData.primary_score || 0)}
            competitorScore={Math.round(shareData.competitor_score || 0)}
            primaryUrl={primary.url}
            competitorUrl={competitor.url}
          />
        ) : (
          <button
            onClick={ensureShareLink}
            disabled={mintingShare}
            data-testid="comparison-mint-share-btn"
            className="px-4 py-2 bg-gradient-to-r from-[#A855F7] to-[#3B82F6] hover:opacity-90 disabled:opacity-50 text-white text-xs font-semibold rounded-lg flex items-center gap-2 transition-all"
          >
            {mintingShare ? (
              <>
                <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <Share size={13} weight="bold" />
                {t('heatmap.get_share_link')}
              </>
            )}
          </button>
        )}
      </div>

      {/* Metrics matrix */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="comparison-metrics">
        <MetricCell
          label={t('heatmap.top_intensity')}
          you={`${Math.round(m.primary_top_intensity * 100)}%`}
          comp={`${Math.round(m.competitor_top_intensity * 100)}%`}
          winnerSide={m.primary_top_intensity > m.competitor_top_intensity ? 'you' : 'comp'}
        />
        <MetricCell
          label={t('heatmap.avg_intensity')}
          you={`${Math.round(m.primary_avg_intensity * 100)}%`}
          comp={`${Math.round(m.competitor_avg_intensity * 100)}%`}
          winnerSide={m.primary_avg_intensity > m.competitor_avg_intensity ? 'you' : 'comp'}
        />
        <MetricCell
          label={t('heatmap.above_the_fold')}
          you={`${m.primary_above_fold}`}
          comp={`${m.competitor_above_fold}`}
          winnerSide={m.primary_above_fold >= m.competitor_above_fold ? 'you' : 'comp'}
        />
        <MetricCell
          label={t('heatmap.ux_grade')}
          you={m.primary_grade || '—'}
          comp={m.competitor_grade || '—'}
          winnerSide="neutral"
        />
      </div>

      {/* Side-by-side canvases */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="side-by-side">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#3B82F6]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[#3B82F6] font-semibold">{t('heatmap.your_page')}</span>
          </div>
          <HeatmapOverlay
            result={primary}
            onZoneClick={onPickPrimary}
            activeZoneIdx={activeIdx}
            t={t}
          />
          <ZoneList zones={primary.zones || []} activeIdx={activeIdx} onPick={onPickPrimary} t={t} />
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#EF4444]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[#EF4444] font-semibold">{t('heatmap.competitor_page')}</span>
          </div>
          <HeatmapOverlay
            result={competitor}
            onZoneClick={onPickCompetitor}
            activeZoneIdx={activeIdxCompetitor}
            t={t}
          />
          <ZoneList zones={competitor.zones || []} activeIdx={activeIdxCompetitor} onPick={onPickCompetitor} t={t} />
        </div>
      </div>

      {/* Action tips */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-5" data-testid="comparison-tips">
        <div className="flex items-center gap-2 mb-4">
          <Warning size={14} weight="bold" className="text-[#F59E0B]" />
          <span className="text-sm font-bold text-white">{t('heatmap.tips_title')}</span>
          <span className="text-xs text-white/40">· {insights?.tips?.length || 0}</span>
        </div>
        <div className="space-y-2">
          {(insights?.tips || []).map((tip, idx) => {
            const color = SEVERITY_COLOR[tip.severity] || SEVERITY_COLOR.low;
            return (
              <div
                key={idx}
                data-testid={`tip-${idx}`}
                className="flex items-start gap-3 p-3 rounded-lg bg-[#0F0F0F] border border-white/5"
              >
                <span
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded shrink-0"
                  style={{ background: `${color}22`, color }}
                >
                  {t(`heatmap.tip_severity_${tip.severity}`) || tip.severity}
                </span>
                <p className="text-xs text-white/80 leading-relaxed">{tip.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


const MetricCell = ({ label, you, comp, winnerSide }) => (
  <div className="p-3 bg-[#0F0F0F] border border-white/10 rounded-xl">
    <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-semibold mb-2">{label}</div>
    <div className="flex items-center justify-between gap-2 text-sm">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] shrink-0" />
        <span
          className={`font-bold truncate ${winnerSide === 'you' ? 'text-[#3B82F6]' : 'text-white/80'}`}
        >
          {you}
        </span>
      </div>
      <span className="text-white/20 text-xs">vs</span>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] shrink-0" />
        <span
          className={`font-bold truncate ${winnerSide === 'comp' ? 'text-[#EF4444]' : 'text-white/80'}`}
        >
          {comp}
        </span>
      </div>
    </div>
  </div>
);


// ─────────────────────── Main page ───────────────────────


function HeatmapPage() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  // Allow viral-loop deep linking via /heatmap?primary=…&competitor=…
  // (set by BattlePreview's CTA so the viewer lands with both URLs ready).
  const initialQuery = (() => {
    if (typeof window === 'undefined') return { mode: 'single', primary: '', competitor: '' };
    const p = new URLSearchParams(window.location.search);
    const primary = p.get('primary') || '';
    const competitor = p.get('competitor') || '';
    return {
      mode: primary && competitor ? 'compare' : 'single',
      primary,
      competitor,
    };
  })();
  const [mode, setMode] = useState(initialQuery.mode); // 'single' | 'compare'
  const [url, setUrl] = useState(initialQuery.primary);
  const [competitorUrl, setCompetitorUrl] = useState(initialQuery.competitor);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [activeIdxCompetitor, setActiveIdxCompetitor] = useState(0);
  const [historyItems, setHistoryItems] = useState([]);
  const resultRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data)).catch(() => {});
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/history`, { withCredentials: true });
      setHistoryItems(res.data.items || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Refetch on brand switch (Agency Mode)
  useEffect(() => {
    const handler = () => { loadHistory(); setResult(null); setComparison(null); };
    window.addEventListener('brand:changed', handler);
    return () => window.removeEventListener('brand:changed', handler);
  }, [loadHistory]);

  const analyze = async (overrideUrl) => {
    const target = (overrideUrl || url).trim();
    if (!target || !(target.startsWith('http://') || target.startsWith('https://'))) {
      toast.error(t('heatmap.error_url'));
      return;
    }
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/analyze`, { url: target }, { withCredentials: true });
      setResult(res.data);
      setComparison(null);
      setActiveIdx(0);
      loadHistory();
      // Scroll into view smoothly
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      if (res.data.cache_hit) toast.info(t('heatmap.cache_hit'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setAnalyzing(false);
    }
  };

  const runCompare = async () => {
    const a = url.trim();
    const b = competitorUrl.trim();
    const isUrl = (u) => u.startsWith('http://') || u.startsWith('https://');
    if (!a || !b || !isUrl(a) || !isUrl(b) || a === b) {
      toast.error(t('heatmap.compare_invalid'));
      return;
    }
    setAnalyzing(true);
    try {
      const res = await axios.post(
        `${API}/compare`,
        { primary_url: a, competitor_url: b },
        { withCredentials: true },
      );
      setComparison(res.data);
      setResult(null);
      setActiveIdx(0);
      setActiveIdxCompetitor(0);
      loadHistory();
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setAnalyzing(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm(t('heatmap.delete_confirm'))) return;
    try {
      await axios.delete(`${API}/${id}`, { withCredentials: true });
      loadHistory();
      if (result?.heatmap_id === id) setResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const grade = result?.ux_grade?.toUpperCase() || 'B';
  const gradeColor = GRADE_COLOR[grade] || '#3B82F6';

  return (
    <DashboardLayout user={user} activePage="/heatmap">
      <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="heatmap-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EF4444] via-[#F97316] to-[#F59E0B] flex items-center justify-center">
              <Flame size={20} weight="bold" className="text-white" />
            </div>
            <h1 className="text-4xl font-[Outfit] font-bold text-white">{t('heatmap.title')}</h1>
          </div>
          <p className="text-white/60 text-sm max-w-2xl">{t('heatmap.subtitle')}</p>
        </div>

        {/* Mode toggle */}
        <div className="flex items-center gap-2 mb-4">
          <button
            data-testid="mode-single"
            onClick={() => { setMode('single'); setComparison(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
              mode === 'single'
                ? 'bg-white/10 text-white border border-white/20'
                : 'text-white/50 hover:text-white border border-transparent'
            }`}
          >
            <Flame size={13} weight="bold" />
            {t('heatmap.mode_single')}
          </button>
          <button
            data-testid="mode-compare"
            onClick={() => { setMode('compare'); setResult(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
              mode === 'compare'
                ? 'bg-gradient-to-r from-[#A855F7]/15 to-[#EF4444]/15 text-white border border-[#A855F7]/40'
                : 'text-white/50 hover:text-white border border-transparent'
            }`}
          >
            <Sword size={13} weight="bold" />
            {t('heatmap.mode_compare')}
          </button>
        </div>

        {/* URL input */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-5 mb-6">
          {mode === 'single' ? (
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  data-testid="heatmap-url-input"
                  type="url"
                  placeholder={t('heatmap.url_placeholder')}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !analyzing) analyze(); }}
                  className="w-full pl-9 pr-3 py-3 bg-[#0F0F0F] border border-white/15 rounded-lg text-white text-sm focus:border-[#F97316] focus:outline-none"
                  disabled={analyzing}
                />
              </div>
              <button
                onClick={() => analyze()}
                disabled={analyzing || !url.trim()}
                data-testid="heatmap-analyze-btn"
                className="px-5 py-3 bg-gradient-to-r from-[#EF4444] to-[#F97316] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-all"
              >
                {analyzing ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t('heatmap.analyzing')}
                  </>
                ) : (
                  <>
                    <Flame size={14} weight="bold" />
                    {t('heatmap.analyze')}
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="compare-inputs">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] font-semibold mb-1.5 block">
                  {t('heatmap.primary_url_label')}
                </label>
                <input
                  data-testid="compare-primary-input"
                  type="url"
                  placeholder={t('heatmap.url_placeholder')}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full px-3 py-3 bg-[#0F0F0F] border border-white/15 rounded-lg text-white text-sm focus:border-[#3B82F6] focus:outline-none"
                  disabled={analyzing}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#EF4444] font-semibold mb-1.5 block">
                  {t('heatmap.competitor_url_label')}
                </label>
                <input
                  data-testid="compare-competitor-input"
                  type="url"
                  placeholder={t('heatmap.competitor_placeholder')}
                  value={competitorUrl}
                  onChange={(e) => setCompetitorUrl(e.target.value)}
                  className="w-full px-3 py-3 bg-[#0F0F0F] border border-white/15 rounded-lg text-white text-sm focus:border-[#EF4444] focus:outline-none"
                  disabled={analyzing}
                />
              </div>
              <button
                onClick={runCompare}
                disabled={analyzing || !url.trim() || !competitorUrl.trim()}
                data-testid="compare-run-btn"
                className="w-full px-5 py-3 bg-gradient-to-r from-[#3B82F6] via-[#A855F7] to-[#EF4444] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 transition-all"
              >
                {analyzing ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t('heatmap.comparing')}
                  </>
                ) : (
                  <>
                    <Sword size={14} weight="bold" />
                    {t('heatmap.compare')}
                  </>
                )}
              </button>
            </div>
          )}
          {!result && !comparison && (
            <p className="text-xs text-white/40 mt-3" data-testid="heatmap-empty-hint">{t('heatmap.empty_hint')}</p>
          )}
        </div>

        {/* Comparison result */}
        {comparison && (
          <ComparisonView
            data={comparison}
            activeIdx={activeIdx}
            activeIdxCompetitor={activeIdxCompetitor}
            onPickPrimary={setActiveIdx}
            onPickCompetitor={setActiveIdxCompetitor}
            t={t}
            forwardRef={resultRef}
          />
        )}

        {/* Result */}
        {result && (
          <div ref={resultRef} data-testid="heatmap-result" className="space-y-5">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                icon={Eye}
                label={t('heatmap.primary_focus')}
                value={result.primary_focus || '—'}
                accent="#EF4444"
                testId="stat-primary-focus"
              />
              <StatCard
                icon={Star}
                label={t('heatmap.ux_grade')}
                value={grade}
                accent={gradeColor}
                testId="stat-ux-grade"
              />
              <StatCard
                icon={Flame}
                label={t('heatmap.zones_label')}
                value={result.zones?.length || 0}
                accent="#F59E0B"
                testId="stat-zones-count"
              />
              <StatCard
                icon={ChartBar}
                label={t('heatmap.intensity')}
                value={
                  result.zones?.length
                    ? `${Math.round((result.zones.reduce((s, z) => s + z.intensity, 0) / result.zones.length) * 100)}%`
                    : '—'
                }
                accent="#3B82F6"
                testId="stat-avg-intensity"
              />
            </div>

            {/* Summary */}
            {result.summary && (
              <div className="p-5 bg-gradient-to-r from-[#EF4444]/10 via-[#F97316]/5 to-transparent border border-[#F97316]/20 rounded-2xl" data-testid="heatmap-summary">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#F97316] font-semibold mb-2">
                  {t('heatmap.summary')}
                </div>
                <p className="text-sm text-white/85 leading-relaxed">{result.summary}</p>
              </div>
            )}

            {/* Canvas + Zone list */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
              <HeatmapOverlay
                result={result}
                onZoneClick={setActiveIdx}
                activeZoneIdx={activeIdx}
                t={t}
              />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-semibold mb-3">
                  {result.zones?.length || 0} {t('heatmap.zones_label')}
                </div>
                <ZoneList
                  zones={result.zones || []}
                  activeIdx={activeIdx}
                  onPick={setActiveIdx}
                  t={t}
                />
              </div>
            </div>
          </div>
        )}

        {/* History */}
        <div className="mt-10">
          <div className="text-sm font-bold text-white mb-3">{t('heatmap.history_title')}</div>
          {historyItems.length === 0 ? (
            <div className="text-sm text-white/40 py-6" data-testid="heatmap-history-empty">
              {t('heatmap.history_empty')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="heatmap-history">
              {historyItems.map((h) => (
                <div
                  key={h.heatmap_id}
                  className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 hover:border-white/25 transition-all"
                  data-testid={`history-item-${h.heatmap_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-white/40 mb-1">{t('heatmap.analyzed')}</div>
                      <div className="text-sm font-semibold text-white truncate">
                        {(h.page_meta?.title || h.url).slice(0, 60)}
                      </div>
                      <div className="text-[11px] text-white/50 truncate font-mono">{h.url.replace(/https?:\/\//, '')}</div>
                    </div>
                    <button
                      onClick={() => removeItem(h.heatmap_id)}
                      className="text-white/30 hover:text-[#EF4444] transition-colors"
                      aria-label="delete"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/50">{h.zones?.length || 0} {t('heatmap.zones_label')}</span>
                    <button
                      onClick={() => { setResult(h); setActiveIdx(0); resultRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
                      className="text-[#F97316] hover:text-[#FB923C] font-semibold flex items-center gap-1 transition-colors"
                    >
                      {t('heatmap.view')} <ArrowRight size={11} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default HeatmapPage;
